import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Injectable, OnDestroy } from "@angular/core";
import { AccountAppValue, RewardStateFilterType } from "@fabric-types/app-account";
import { Store, select } from "@ngrx/store";
import { RegisterSuccessQueryParams } from "@obg/mat.ui.common/customer-verification";
import { CustomerOfferSelector, PaymentQueryParams, VerificationType } from "@obg/ngx.common";
import { CORE_ROUTE, CoreState, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { isEmpty } from "lodash-es";
import { Observable, Subscription, combineLatest, merge } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { LoginFlowLoaderService } from "./login-flow-loader.service";
import { JurisdictionFlowSteps, MgaFlowSteps, SgaFlowSteps } from "./login-flow.const";
import { StepperService } from "./stepper.service";
import { VerificationData } from "./verification.model";

@Injectable()
export class VerificationService implements OnDestroy {
	private flowSteps: JurisdictionFlowSteps = MgaFlowSteps;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private loginFlowLoaderService: LoginFlowLoaderService,
		private stepperService: StepperService,
		private customerOfferSelector: CustomerOfferSelector,
		private documentRef: DocumentRef
	) {}

	init() {
		this.data$$ = merge(
			this.jurisdiction$(),
			this.verificationData$(),
			this.loading$(),
			this.hasNavigatedToTermsAndConditions$(),
			this.depositBonus$(),
			this.rewardAccepted$()
		).subscribe();
	}

	jurisdiction$() {
		return this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				switch (jurisdiction) {
					case Jurisdiction.Sga:
						this.flowSteps = SgaFlowSteps;
						break;
					default:
						this.flowSteps = MgaFlowSteps;
						break;
				}
			}),
			map(() => undefined)
		);
	}

	verificationData$(): Observable<VerificationData> {
		return this.store.select<RegisterSuccessQueryParams | undefined>(RouteSelector.getQueryParams).pipe(
			ignoreNil(),
			filter((params) => !!params.variant),
			map((params) => ({ name: params.name, variant: params.variant }))
		);
	}

	loading$(): Observable<VerificationData> {
		return this.verificationData$().pipe(
			tap(() => {
				this.loginFlowLoaderService.toggleLoading(false);
			})
		);
	}

	hasNavigatedToTermsAndConditions$() {
		return this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			tap(({ name }) => {
				if (name === CORE_ROUTE.termsAndConditions) {
					this.stepperService.navigate(false);
				}
			})
		);
	}

	depositBonus$() {
		return combineLatest([
			this.store.select<PaymentQueryParams | undefined>(RouteSelector.getQueryParams),
			new Observable(
				this.documentRef.obgEventBus.retrieve(AccountAppValue.GetDepositRewards, {
					rewardFilter: RewardStateFilterType.Claimable,
				})
			),
			this.store.select(this.customerOfferSelector.getOffers()),
			this.verificationData$(),
		]).pipe(
			filter(([, , , verificationData]) => !!verificationData.variant),
			take(1),
			tap(([params, rewards, offers, verificationData]) => {
				if (
					verificationData.variant !== VerificationType.withRewards ||
					(isEmpty(rewards) && isEmpty(offers)) ||
					(params &&
						(params.rewardIdentifier ||
							params.siteTrackingId ||
							coerceBooleanProperty(params.skipRewardClaim)))
				) {
					this.stepperService.navigateToSelectedStep(this.flowSteps.Deposit);
				} else {
					this.stepperService.navigate(true);
				}
			})
		);
	}

	rewardAccepted$() {
		return this.store.select<PaymentQueryParams | undefined>(RouteSelector.getQueryParams).pipe(
			filter(
				(params) =>
					!!(
						params &&
						(params.rewardIdentifier ||
							params.siteTrackingId ||
							coerceBooleanProperty(params.skipRewardClaim))
					)
			),
			tap(() => this.stepperService.navigate(true))
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}
}
